import React, { useState, useCallback, useRef, useContext } from 'react';
import CIcon from '@coreui/icons-react';
import XLSX from 'xlsx';

import { LookupTableContext } from './LookupTable';
import ImportTableModal from './ImportTableModal';
import { useOutsideHandling } from '../../../../../../utils';

const automaticVariableText = '// required (optional if selecting exists with any value or does not exist operator), any string'
const optionalText = '// optional, any string';
const regexHeaderText = '// required, accepts a 1 or a 0';
const requiredText = '// required, accepts a 1 or a 0';

const ImportExportTable = () => {
    const { tableRow, IsExisted } = useContext(LookupTableContext);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showImportExport, setShowImportExport] = useState(false);

    function capitalizeFirstLetter(string) {
        return string[0].toUpperCase() + string.slice(1);
    }

    const getHeaderValues = () => {
        let firstRowValues = [];
        let secondRowValues = [];

        // Convert conditions to arrays which are compatible with XLSX
        if (Array.isArray(tableRow) && Array.isArray(tableRow[0].conditions)) {
            tableRow[0].conditions.forEach(condition => {
                let conditionValue = condition['g0'];
                let found = IsExisted(conditionValue.key, conditionValue.type);

                if (found) {
                    let secondRowName = found.fullName;

                    switch (conditionValue.type) {
                        case 'Variable':
                            firstRowValues.push(automaticVariableText, regexHeaderText);
                            secondRowValues.push(secondRowName, secondRowName + '.regex');
                            break;
                        case 'Event':
                        case 'MyEventNative':
                            // Event with listener type is Native DataLayer, found.code is undefined, so use found.key
                            secondRowName = found.code || found.key;
                            firstRowValues.push(requiredText);
                            secondRowValues.push(secondRowName);
                            break;
                        case 'NativeBrowserStorage':
                            // Event with listener type is Native Browser Storage, found.code is undefined, so use found.key
                            let label = `${capitalizeFirstLetter(found.key.type)}: ${found.key.name}, Format: ${capitalizeFirstLetter(found.key.format)}`
                            firstRowValues.push(automaticVariableText, regexHeaderText);
                            secondRowValues.push(label, `(${label}).regex`);
                            break;
                        default:
                            secondRowName = secondRowName || found.code || '';
                            firstRowValues.push(requiredText);
                            secondRowValues.push(secondRowName);
                    }
                }
            })
        }

        // Convert custom variables to arrays which are compatible with XLSX
        if (Array.isArray(tableRow) && Array.isArray(tableRow[0].customVariables)) {
            tableRow[0].customVariables.forEach(() => {
                firstRowValues.push(optionalText);
                secondRowValues.push('triggerName');
            })
        }

        return { firstRowValues, secondRowValues };
    }

    const exportToFile = (type = 'csv', fileName = 'table-import-export', onlyRow1And2) => {
        let { firstRowValues, secondRowValues } = getHeaderValues();

        // Many next rows
        let manyRowValues = [];

        if (!onlyRow1And2) {
            // Convert table rows, which begin from row fourth to arrays which are compatible with XLSX
            if (Array.isArray(tableRow)) {
                tableRow.forEach(({ conditions, customVariables }) => {
                    let rowValues = [];

                    if (Array.isArray(conditions)) {
                        conditions.forEach(condition => {
                            let conditionValue = condition['g0'];
                            let itemValue = '';

                            switch (conditionValue.type) {
                                case 'Variable':
                                case 'NativeBrowserStorage':
                                case 'Selector':
                                    rowValues.push(conditionValue.value, conditionValue.isRegex ? '1' : '0');
                                    break;
                                case 'Event':
                                case 'MyEventNative':
                                    if (conditionValue.value === 'true') {
                                        itemValue = '1';
                                    } else if (conditionValue.value === 'false') {
                                        itemValue = '0';
                                    }

                                    rowValues.push(itemValue);
                                    break;
                                default:
                                    rowValues.push(conditionValue.value);
                            }
                        })
                    }

                    if (Array.isArray(customVariables)) {
                        customVariables.forEach(customVariable => {
                            rowValues.push(customVariable.id);
                        })
                    }

                    manyRowValues.push(rowValues);
                })
            }
        }

        let dataToExport = [
            firstRowValues,
            secondRowValues,
            ...manyRowValues
        ];

        if (type === 'csv') {
            // https://reactjs.org/docs/code-splitting.html#import
            import('../../../../../../utils').then(utils => {
                // csv type will use this function to export correctly
                utils.exportToCSV(`${fileName}.${type}`, dataToExport);
            });
        } else {
            let workSheet = XLSX.utils.aoa_to_sheet(dataToExport);

            // Add to workbook
            let workBook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workBook, workSheet, 'Sheet1');

            // If uses XLSX.writeFile to export csv file, open that file then Ctrl + S to save,
            // then close file without saving changes, open that file again all values 
            // of each rows will merge into the first column of that row, so use another function to export csv file
            XLSX.writeFile(workBook, `${fileName}.${type}`);
        }
    }

    const handClickImportExport = useCallback(() => {
        setTimeout(() => {
            setShowImportExport(!showImportExport)
        }, 100);
    }, [showImportExport])

    const handleClickOutside = useCallback(() => {
        if (!showImportModal) {
            setShowImportExport(false)
        }
    }, [showImportModal])

    const wrapperRef = useRef(null);
    useOutsideHandling(wrapperRef, handleClickOutside);

    const toggleImportModal = useCallback(() => {
        if (!showImportModal) {
            setShowImportExport(false);
        }

        setShowImportModal(!showImportModal);
    }, [showImportModal])

    return (
        <>
            <div className="import-export-wrapper">
                <div className="import-export">
                    <CIcon icon="cil-chevron-double-down" height={14} />
                    <button type="button" onClick={handClickImportExport}>Import/Export</button>
                </div>
                <ul ref={wrapperRef} className={showImportExport ? '' : 'hidden'}>
                    <li>
                        <button color="primary" type="button" onClick={() => exportToFile('csv')}><CIcon icon="cil-cloud-download" height={14}></CIcon><span>Export existing table values</span></button>
                    </li>
                    <li>
                        <button color="primary" type="button" onClick={toggleImportModal}><CIcon icon="cil-cloud-upload" height={14}></CIcon><span>Import new table values</span></button>
                    </li>
                </ul>
            </div>
            <ImportTableModal
                show={showImportModal}
                onClose={() => setShowImportModal(false)}
                toggleModal={toggleImportModal}
                exportToFile={exportToFile}
                getHeaderValues={getHeaderValues}
            />
        </>

    )
}

export default ImportExportTable
